import React, { useState, useEffect } from 'react';
import './App.css';
import Components from './Components';
import AdminInterface from './AdminInterface';
import { eventData, contactData, theaterConfig } from './data';
import apiService from './api';

const {
  Header,
  HeroSection,
  NewsletterSignup,
  RentalInformation,
  FeaturedEvent,
  UpcomingEvents,
  AboutSection,
  GalleryPage,
  Footer,
  EventCalendar,
  EventDetail,
  FAQSection,
  EmpressTavern
} = Components;

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [events, setEvents] = useState([]);
  // Initialize contacts with safe default to prevent undefined errors
  const [contacts, setContacts] = useState(contactData || {
    phone: '(916) 476-3356',
    email: 'info@cresttheater.com',
    address: {
      street: '1013 K Street',
      city: 'Sacramento',
      state: 'CA',
      zip: '95814'
    },
    socialMedia: {
      facebook: '#',
      twitter: '#',
      instagram: '#',
      spotify: '#'
    }
  });
  const [currentView, setCurrentView] = useState('home'); // 'home', 'calendar', 'gallery'
  const [selectedEvent, setSelectedEvent] = useState(null); // For event detail modal
  const [showAllEvents, setShowAllEvents] = useState(false); // For show more events functionality
  const [isLoading, setIsLoading] = useState(true); // Loading state for initial data fetch
  
  // Initialize featured event with API preference or auto-select next event
  const [featuredEvent, setFeaturedEvent] = useState(() => {
    // Start with default from data file, will be updated by API
    const now = new Date();
    const upcomingEvents = eventData.upcomingEvents
      .filter(event => new Date(event.date) >= now)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    
    return upcomingEvents.length > 0 ? upcomingEvents[0] : eventData.featuredEvent;
  });
  
  // Gallery Images State - starts empty, loads from API
  const [galleryImages, setGalleryImages] = useState([]);

  // Page Images State - starts with default, loads from API
  const [pageImages, setPageImages] = useState({
    hero: 'https://i.imgur.com/vi3Cqu1.jpeg',
    faq: 'https://i.imgur.com/CvzwGq0.jpeg', 
    empress: 'https://crestsacra1stg.wpenginepowered.com/wp-content/uploads/2020/03/photo-4-1024x768-1.jpg',
    rental: 'https://i.imgur.com/b1k6F4U.jpeg'
  });

  // Banner State - starts with default, loads from API
  const [bannerSettings, setBannerSettings] = useState({
    text: '',
    fontFamily: 'Inter',
    fontSize: '24',
    isBold: true,
    isItalic: false,
    isUnderline: false
  });

  // Save gallery images to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('crestTheaterGallery', JSON.stringify(galleryImages));
  }, [galleryImages]);

  // Save page images to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('crestTheaterPageImages', JSON.stringify(pageImages));
  }, [pageImages]);

  // Save banner settings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('crestTheaterBannerSettings', JSON.stringify(bannerSettings));
  }, [bannerSettings]);

  // Empress Tavern Settings State - starts with default, loads from API
  const [empressSettings, setEmpressSettings] = useState({
    headerSubtext: 'Coming Soon - A Unique Sacramento Food Venue',
    partnershipDescription: 'The owners of Empress Tavern are partnering with <a href="https://alchemistcdc.org/" target="_blank" rel="noopener noreferrer" class="text-red-600 hover:text-red-800 underline">Alchemist CDC</a> to launch a truly unique Sacramento food venue.',
    redBoxText: 'Grand Opening<br><span class="text-3xl font-bold text-red-600">July 2025</span>',
    whiteBoxText: 'Media Inquiries & Booking Information<br><br><strong>Email:</strong> <a href="mailto:info@empresstavern.com" class="text-red-600 hover:text-red-800 underline">info@empresstavern.com</a><br><strong>Partnership Inquiries:</strong> <a href="mailto:shannin@alchemistcdc.com" class="text-red-600 hover:text-red-800 underline">shannin@alchemistcdc.com</a>',
    aboutPartnership: '<h3 class="text-2xl font-bold text-gray-800 mb-4">About This Partnership</h3><p class="text-lg leading-relaxed text-gray-700 mb-4">This exciting collaboration between Empress Tavern and Alchemist CDC represents a new chapter in Sacramento\'s dining and entertainment scene. Located within the historic Crest Theater, this venue will offer a unique culinary experience that complements the theater\'s rich cultural heritage.</p><p class="text-lg leading-relaxed text-gray-700 mb-4">Stay tuned for updates on menu offerings, special events, and opening details as we approach the grand opening in July 2025.</p>'
  });

  // Save empress settings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('crestTheaterEmpressSettings', JSON.stringify(empressSettings));
  }, [empressSettings]);

  // Load initial data from backend with localStorage fallback
  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoading(true);
      try {
        // NUCLEAR OPTION: Clear ALL possible localStorage corruption
        localStorage.removeItem('crestTheaterEvents');
        localStorage.removeItem('crestTheaterFeaturedEventId');
        localStorage.removeItem('crestTheaterGallery');
        localStorage.removeItem('crestTheaterContacts');
        localStorage.removeItem('crestTheaterPageImages');
        localStorage.removeItem('crestTheaterBannerSettings');
        localStorage.removeItem('crestTheaterEmpressSettings');
        console.log('🧹 CLEARED ALL localStorage to prevent data corruption');
        
        // Load events from backend
        const backendEvents = await apiService.getEvents();
        console.log('🔍 Raw backend events:', backendEvents);
        
        if (backendEvents && Array.isArray(backendEvents)) {
          // Convert backend field names to frontend format
          const frontendEvents = backendEvents.map(event => {
            console.log('🔍 Mapping event:', event.id, event.title);
            return {
              id: event.id,
              title: event.title,
              date: event.date,
              time: event.time || "",
              category: event.category,
              ticketPrice: event.ticket_price || "",
              description: event.description,
              image: event.image_url || "", // Convert image_url to image
              ticketingAgency: event.ticketing_agency || "Ticketmaster",
              ticketLink: event.ticket_url || ""
            };
          });
          
          console.log('🔍 Final frontend events:', frontendEvents.map(e => ({id: e.id, title: e.title})));
          setEvents(frontendEvents);
          // Save the clean backend data to localStorage
          localStorage.setItem('crestTheaterEvents', JSON.stringify(frontendEvents));
          console.log('✅ Events loaded from backend and synced:', frontendEvents.length);
        } else {
          // If backend has no events, use empty array (not default data)
          setEvents([]);
          localStorage.setItem('crestTheaterEvents', JSON.stringify([]));
          console.log('📦 No events in backend, starting with empty array');
        }

        // Load contacts from backend
        try {
          const backendContacts = await apiService.getContacts();
          if (backendContacts && backendContacts.socialMedia) {
            // Ensure socialMedia object exists with all required fields
            const safeBackendContacts = {
              ...backendContacts,
              socialMedia: {
                facebook: '#',
                twitter: '#',
                instagram: '#',
                spotify: '#',
                ...backendContacts.socialMedia
              }
            };
            setContacts(safeBackendContacts);
            console.log('✅ Contacts loaded from backend');
          }
        } catch (error) {
          console.warn('📦 Contacts API not available, using localStorage');
          const savedContacts = localStorage.getItem('crestTheaterContacts');
          if (savedContacts) {
            const parsedContacts = JSON.parse(savedContacts);
            // Ensure socialMedia exists
            const safeContacts = {
              ...parsedContacts,
              socialMedia: {
                facebook: '#',
                twitter: '#',
                instagram: '#',
                spotify: '#',
                ...parsedContacts.socialMedia
              }
            };
            setContacts(safeContacts);
          }
          // If no localStorage, keep the default from initialization
        }

        // Load featured event selection from backend
        try {
          const backendFeaturedSelection = await apiService.getFeaturedEventSelection();
          if (backendFeaturedSelection && backendFeaturedSelection.selected_event_id) {
            // Will be set once events are loaded
            console.log('✅ Featured event selection loaded from backend:', backendFeaturedSelection.selected_event_id);
            localStorage.setItem('crestTheaterFeaturedEventId', backendFeaturedSelection.selected_event_id);
          }
        } catch (error) {
          console.warn('📦 Featured event selection API not available, using localStorage');
        }

        // Load gallery images from backend
        try {
          const backendGalleryImages = await apiService.getGalleryImages();
          if (backendGalleryImages && Array.isArray(backendGalleryImages)) {
            setGalleryImages(backendGalleryImages);
            console.log('✅ Gallery images loaded from backend:', backendGalleryImages.length);
          } else {
            // Fallback to localStorage then default images
            const savedGallery = localStorage.getItem('crestTheaterGallery');
            if (savedGallery) {
              setGalleryImages(JSON.parse(savedGallery));
              console.log('📦 Gallery images loaded from localStorage fallback');
            } else {
              setGalleryImages(theaterConfig.galleryImages);
              console.log('🏠 Gallery images loaded from default data');
            }
          }
        } catch (error) {
          console.warn('📦 Gallery API not available, using localStorage');
          const savedGallery = localStorage.getItem('crestTheaterGallery');
          if (savedGallery) {
            setGalleryImages(JSON.parse(savedGallery));
          } else {
            setGalleryImages(theaterConfig.galleryImages);
          }
        }

        // Load banner settings from backend
        try {
          const backendBannerSettings = await apiService.getBannerSettings();
          if (backendBannerSettings) {
            // Convert backend field names to frontend format
            const frontendBannerSettings = {
              text: backendBannerSettings.text,
              fontFamily: backendBannerSettings.font_family,
              fontSize: backendBannerSettings.font_size,
              isBold: backendBannerSettings.is_bold,
              isItalic: backendBannerSettings.is_italic,
              isUnderline: backendBannerSettings.is_underlined
            };
            setBannerSettings(frontendBannerSettings);
            console.log('✅ Banner settings loaded from backend');
          } else {
            // Fallback to localStorage
            const savedBanner = localStorage.getItem('crestTheaterBannerSettings');
            if (savedBanner) {
              setBannerSettings(JSON.parse(savedBanner));
              console.log('📦 Banner settings loaded from localStorage fallback');
            }
          }
        } catch (error) {
          console.warn('📦 Banner settings API not available, using localStorage');
          const savedBanner = localStorage.getItem('crestTheaterBannerSettings');
          if (savedBanner) {
            setBannerSettings(JSON.parse(savedBanner));
          }
        }

        // Load page images from backend
        try {
          const backendPageImages = await apiService.getPageImages();
          if (backendPageImages) {
            const frontendPageImages = {
              hero: backendPageImages.hero,
              faq: backendPageImages.faq,
              empress: backendPageImages.empress,
              rental: backendPageImages.rental
            };
            setPageImages(frontendPageImages);
            console.log('✅ Page images loaded from backend');
          } else {
            // Fallback to localStorage
            const savedPageImages = localStorage.getItem('crestTheaterPageImages');
            if (savedPageImages) {
              setPageImages(JSON.parse(savedPageImages));
              console.log('📦 Page images loaded from localStorage fallback');
            }
          }
        } catch (error) {
          console.warn('📦 Page images API not available, using localStorage');
          const savedPageImages = localStorage.getItem('crestTheaterPageImages');
          if (savedPageImages) {
            setPageImages(JSON.parse(savedPageImages));
          }
        }

        // Load empress settings from backend
        try {
          const backendEmpressSettings = await apiService.getEmpressSettings();
          if (backendEmpressSettings) {
            // Convert backend field names to frontend format
            const frontendEmpressSettings = {
              headerSubtext: backendEmpressSettings.header_subtext,
              partnershipDescription: backendEmpressSettings.partnership_description,
              redBoxText: backendEmpressSettings.red_box_announcement,
              whiteBoxText: backendEmpressSettings.contact_info,
              aboutPartnership: backendEmpressSettings.about_partnership
            };
            setEmpressSettings(frontendEmpressSettings);
            console.log('✅ Empress settings loaded from backend');
          } else {
            // Fallback to localStorage
            const savedEmpress = localStorage.getItem('crestTheaterEmpressSettings');
            if (savedEmpress) {
              setEmpressSettings(JSON.parse(savedEmpress));
              console.log('📦 Empress settings loaded from localStorage fallback');
            }
          }
        } catch (error) {
          console.warn('📦 Empress settings API not available, using localStorage');
          const savedEmpress = localStorage.getItem('crestTheaterEmpressSettings');
          if (savedEmpress) {
            setEmpressSettings(JSON.parse(savedEmpress));
          }
        }

      } catch (error) {
        console.error('❌ Failed to load initial data:', error);
        // Fallback to localStorage and default data
        const savedEvents = localStorage.getItem('crestTheaterEvents');
        if (savedEvents) {
          setEvents(JSON.parse(savedEvents));
        } else {
          setEvents(eventData.upcomingEvents);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Helper function to ensure event has correct frontend field mapping
  const mapEventFields = (event) => {
    return {
      id: event.id,
      title: event.title,
      date: event.date,
      time: event.time || "",
      category: event.category,
      ticketPrice: event.ticketPrice || event.ticket_price || "",
      description: event.description,
      image: event.image || event.image_url,
      ticketingAgency: event.ticketingAgency || event.ticketing_agency,
      ticketLink: event.ticketLink || event.ticket_url,
      subtitle: event.subtitle || "",
      location: event.location || "",
      ticketInfo: event.ticketInfo || "",
      presale: event.presale || "",
      onSale: event.onSale || ""
    };
  };

  // Update featured event when events change (for localStorage fallback)
  useEffect(() => {
    if (events.length > 0 && !isLoading) {
      const savedFeaturedId = localStorage.getItem('crestTheaterFeaturedEventId');
      if (savedFeaturedId) {
        const selectedEvent = events.find(event => event.id && event.id.toString() === savedFeaturedId);
        if (selectedEvent) {
          setFeaturedEvent(mapEventFields(selectedEvent));
          return;
        }
      }
      
      // Auto-select next upcoming event if no specific featured event
      const now = new Date();
      const upcomingEvents = events
        .filter(event => new Date(event.date) >= now)
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      
      if (upcomingEvents.length > 0) {
        setFeaturedEvent(mapEventFields(upcomingEvents[0]));
      }
    }
  }, [events, isLoading]);

  // Functions for updating data with backend + localStorage fallback
  const updateEvents = async (newEvents) => {
    try {
      // Update backend - Note: We'll handle individual event creation in AdminInterface
      setEvents(newEvents);
      localStorage.setItem('crestTheaterEvents', JSON.stringify(newEvents));
      console.log('✅ Events updated successfully');
    } catch (error) {
      console.error('❌ Failed to update events:', error);
      // Still update locally for user experience
      setEvents(newEvents);
      localStorage.setItem('crestTheaterEvents', JSON.stringify(newEvents));
    }
  };

  const deleteEvent = async (eventId) => {
    try {
      console.log('🗑️ Attempting to delete event with ID:', eventId);
      console.log('🗑️ Current events in frontend:', events.map(e => ({id: e.id, title: e.title})));
      
      // Always try to delete from backend first
      try {
        await apiService.deleteEvent(eventId);
        console.log('✅ Event deleted from backend database');
      } catch (apiError) {
        console.warn('⚠️ Backend deletion failed (event may not exist in database):', apiError.message);
      }
      
      // Always remove from frontend regardless of backend result
      const updatedEvents = events.filter(event => event.id !== eventId);
      setEvents(updatedEvents);
      localStorage.setItem('crestTheaterEvents', JSON.stringify(updatedEvents));
      console.log('✅ Event removed from frontend display');
      
      // If the deleted event was the featured event, reset featured event
      if (featuredEvent && featuredEvent.id === eventId) {
        const now = new Date();
        const upcomingEvents = updatedEvents
          .filter(event => new Date(event.date) >= now)
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        
        if (upcomingEvents.length > 0) {
          setFeaturedEvent(mapEventFields(upcomingEvents[0]));
        } else {
          setFeaturedEvent(null);
        }
      }
      
      return true;
    } catch (error) {
      console.error('❌ Delete operation failed:', error);
      return false;
    }
  };

  const updateContacts = async (newContacts) => {
    // Ensure newContacts is never undefined or missing socialMedia
    const safeNewContacts = {
      ...contacts, // Start with current safe contacts
      ...newContacts, // Override with new data
      socialMedia: {
        ...contacts.socialMedia, // Preserve existing social media
        ...(newContacts.socialMedia || {}) // Override with new social media if provided
      }
    };
    
    try {
      await apiService.updateContacts(safeNewContacts);
      setContacts(safeNewContacts);
      localStorage.setItem('crestTheaterContacts', JSON.stringify(safeNewContacts));
      console.log('✅ Contacts updated successfully');
    } catch (error) {
      console.error('❌ Failed to update contacts:', error);
      // Still update locally for user experience
      setContacts(safeNewContacts);
      localStorage.setItem('crestTheaterContacts', JSON.stringify(safeNewContacts));
    }
  };

  const updateFeaturedEvent = async (newFeaturedEvent) => {
    try {
      // Ensure the featured event has correct field mapping
      const mappedFeaturedEvent = mapEventFields(newFeaturedEvent);
      
      // Save the event ID selection to backend
      const selectionResult = await apiService.updateFeaturedEventSelection(newFeaturedEvent.id);
      if (selectionResult) {
        setFeaturedEvent(mappedFeaturedEvent);
        localStorage.setItem('crestTheaterFeaturedEventId', newFeaturedEvent.id.toString());
        console.log('✅ Featured event selection updated successfully via API');
      } else {
        // API call failed, still update locally
        setFeaturedEvent(mappedFeaturedEvent);
        localStorage.setItem('crestTheaterFeaturedEventId', newFeaturedEvent.id.toString());
        console.log('📦 Featured event updated locally (API failed)');
      }
    } catch (error) {
      console.error('❌ Failed to update featured event:', error);
      // Still update locally for user experience
      setFeaturedEvent(mapEventFields(newFeaturedEvent));
      localStorage.setItem('crestTheaterFeaturedEventId', newFeaturedEvent.id.toString());
    }
  };

  const updateGalleryImages = async (newGalleryImages) => {
    try {
      const updatedImages = await apiService.updateGalleryImages(newGalleryImages);
      if (updatedImages !== null) { // Changed: null check instead of truthy check
        setGalleryImages(updatedImages);
        localStorage.setItem('crestTheaterGallery', JSON.stringify(updatedImages));
        console.log('✅ Gallery images updated successfully via API');
      } else {
        // API call failed, still update locally
        setGalleryImages(newGalleryImages);
        localStorage.setItem('crestTheaterGallery', JSON.stringify(newGalleryImages));
        console.log('📦 Gallery images updated locally (API failed)');
      }
    } catch (error) {
      console.error('❌ Failed to update gallery images:', error);
      // Still update locally for user experience
      setGalleryImages(newGalleryImages);
      localStorage.setItem('crestTheaterGallery', JSON.stringify(newGalleryImages));
    }
  };

  const updateBannerSettings = async (newBannerSettings) => {
    try {
      // Convert frontend field names to backend format
      const backendBannerSettings = {
        text: newBannerSettings.text,
        font_family: newBannerSettings.fontFamily,
        font_size: newBannerSettings.fontSize,
        is_bold: newBannerSettings.isBold,
        is_italic: newBannerSettings.isItalic,
        is_underlined: newBannerSettings.isUnderline
      };
      
      const updatedBanner = await apiService.updateBannerSettings(backendBannerSettings);
      if (updatedBanner) {
        setBannerSettings(newBannerSettings);
        localStorage.setItem('crestTheaterBannerSettings', JSON.stringify(newBannerSettings));
        console.log('✅ Banner settings updated successfully via API');
      } else {
        // API call failed, still update locally
        setBannerSettings(newBannerSettings);
        localStorage.setItem('crestTheaterBannerSettings', JSON.stringify(newBannerSettings));
        console.log('📦 Banner settings updated locally (API failed)');
      }
    } catch (error) {
      console.error('❌ Failed to update banner settings:', error);
      // Still update locally for user experience
      setBannerSettings(newBannerSettings);
      localStorage.setItem('crestTheaterBannerSettings', JSON.stringify(newBannerSettings));
    }
  };

  const updatePageImages = async (newPageImages) => {
    try {
      const updatedPageImages = await apiService.updatePageImages(newPageImages);
      if (updatedPageImages) {
        setPageImages(newPageImages);
        localStorage.setItem('crestTheaterPageImages', JSON.stringify(newPageImages));
        console.log('✅ Page images updated successfully via API');
      } else {
        // API call failed, still update locally
        setPageImages(newPageImages);
        localStorage.setItem('crestTheaterPageImages', JSON.stringify(newPageImages));
        console.log('📦 Page images updated locally (API failed)');
      }
    } catch (error) {
      console.error('❌ Failed to update page images:', error);
      // Still update locally for user experience
      setPageImages(newPageImages);
      localStorage.setItem('crestTheaterPageImages', JSON.stringify(newPageImages));
    }
  };

  const updateEmpressSettings = async (newEmpressSettings) => {
    try {
      // Convert frontend field names to backend format
      const backendEmpressSettings = {
        header_subtext: newEmpressSettings.headerSubtext,
        partnership_description: newEmpressSettings.partnershipDescription,
        red_box_announcement: newEmpressSettings.redBoxText,
        contact_info: newEmpressSettings.whiteBoxText,
        about_partnership: newEmpressSettings.aboutPartnership
      };
      
      const updatedEmpress = await apiService.updateEmpressSettings(backendEmpressSettings);
      if (updatedEmpress) {
        setEmpressSettings(newEmpressSettings);
        localStorage.setItem('crestTheaterEmpressSettings', JSON.stringify(newEmpressSettings));
        console.log('✅ Empress settings updated successfully via API');
      } else {
        // API call failed, still update locally
        setEmpressSettings(newEmpressSettings);
        localStorage.setItem('crestTheaterEmpressSettings', JSON.stringify(newEmpressSettings));
        console.log('📦 Empress settings updated locally (API failed)');
      }
    } catch (error) {
      console.error('❌ Failed to update empress settings:', error);
      // Still update locally for user experience
      setEmpressSettings(newEmpressSettings);
      localStorage.setItem('crestTheaterEmpressSettings', JSON.stringify(newEmpressSettings));
    }
  };

  // Add keyboard listener for CTRL+A admin access
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.key === 'a') {
        event.preventDefault(); // Prevent default select all behavior
        // Trigger admin panel (handled by AdminInterface component)
        const adminEvent = new CustomEvent('toggleAdmin');
        window.dispatchEvent(adminEvent);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleShowMoreEvents = () => {
    setShowAllEvents(true);
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
  };

  const closeEventDetail = () => {
    setSelectedEvent(null);
  };

  // Get events for calendar view - only show upcoming events (no featured event to avoid duplicates)
  const getEventsForCalendar = () => {
    return events.filter(event => event.date);
  };

  return (
    <div className="App">
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading Crest Theater data...</p>
          </div>
        </div>
      )}
      
      <Header 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen}
        theaterName={theaterConfig.name}
        contacts={contacts}
        currentView={currentView}
        setCurrentView={setCurrentView}
      />
      
      <main>
        {currentView === 'home' && (
          <>
            {/* Header/Logo Section */}
            <HeroSection 
              theaterName={theaterConfig.name} 
              subtitle={theaterConfig.subtitle}
              description={theaterConfig.description}
              pageImages={pageImages}
              bannerSettings={bannerSettings}
            />
            
            {/* Next Event (Featured Event) */}
            <FeaturedEvent event={featuredEvent} onEventClick={handleEventClick} />
            
            {/* Upcoming Events */}
            <UpcomingEvents 
              events={events} 
              onEventClick={handleEventClick}
              galleryImages={galleryImages}
              showAllEvents={showAllEvents}
              onShowMoreEvents={handleShowMoreEvents}
            />
            
            {/* Newsletter Signup */}
            <NewsletterSignup />
            
            {/* Rental Information */}
            <RentalInformation pageImages={pageImages} />
          </>
        )}

        {currentView === 'about' && (
          <AboutSection theaterName={theaterConfig.name} />
        )}

        {currentView === 'faq' && (
          <FAQSection theaterName={theaterConfig.name} setCurrentView={setCurrentView} pageImages={pageImages} />
        )}

        {currentView === 'empress' && (
          <EmpressTavern 
            theaterName={theaterConfig.name} 
            setCurrentView={setCurrentView} 
            pageImages={pageImages} 
            empressSettings={empressSettings}
          />
        )}

        {currentView === 'calendar' && (
          <EventCalendar 
            events={getEventsForCalendar()} 
            theaterName={theaterConfig.name}
            onEventClick={handleEventClick}
          />
        )}

        {currentView === 'gallery' && (
          <GalleryPage 
            images={galleryImages}
            theaterName={theaterConfig.name}
          />
        )}
      </main>
      
      <Footer 
        theaterName={theaterConfig.name}
        contacts={contacts}
      />

      {/* Event Detail Modal */}
      {selectedEvent && (
        <EventDetail 
          event={selectedEvent}
          onClose={closeEventDetail}
        />
      )}

      {/* Admin Interface */}
      <AdminInterface
        events={events}
        setEvents={updateEvents}
        deleteEvent={deleteEvent}
        featuredEvent={featuredEvent}
        setFeaturedEvent={updateFeaturedEvent}
        galleryImages={galleryImages}
        setGalleryImages={updateGalleryImages}
        contacts={contacts}
        setContacts={updateContacts}
        pageImages={pageImages}
        setPageImages={updatePageImages}
        bannerSettings={bannerSettings}
        setBannerSettings={updateBannerSettings}
        empressSettings={empressSettings}
        setEmpressSettings={updateEmpressSettings}
        apiService={apiService}
        isLoading={isLoading}
      />
    </div>
  );
}

export default App;